import { create } from "zustand";
import { persist } from "zustand/middleware";
import { MAX_SELECTED_PLAYLISTS } from "@web/features/globe/styles.ts";
import {
  defaultLayerState,
  type GlobeLayerId,
  type GlobeLayerState,
} from "@web/features/globe/layers.ts";

export interface GlobeCountryPanel {
  pointId: string;
  countryCode: string;
  label: string;
  artistIds: string[];
  /** When set, list is filtered to this playlist's artists. */
  playlistId: string | null;
  playlistName: string | null;
}

export type GlobeFocusTarget =
  | { kind: "country"; code: string }
  | { kind: "artist"; id: string };

interface GlobeUiState {
  selectedPlaylistIds: string[];
  countryPanel: GlobeCountryPanel | null;
  layers: GlobeLayerState;
  layersCollapsed: boolean;
  /** Pin/country highlighted after a cinematic focus flight. */
  focusedPointId: string | null;
  /** Pending deep-link / sheet focus request consumed by the canvas. */
  pendingFocus: GlobeFocusTarget | null;
  /** After a focus flight, auto-rotate stays off until user input. */
  focusSuspendAutoRotate: boolean;

  selectPlaylist: (id: string) => void;
  deselectPlaylist: (id: string) => void;
  togglePlaylist: (id: string) => void;
  setCountryPanel: (panel: GlobeCountryPanel | null) => void;
  clearCountryPanel: () => void;
  setLayer: (id: GlobeLayerId, on: boolean) => void;
  toggleLayer: (id: GlobeLayerId) => void;
  setLayersCollapsed: (collapsed: boolean) => void;
  setFocusedPointId: (id: string | null) => void;
  requestFocus: (target: GlobeFocusTarget) => void;
  consumePendingFocus: () => GlobeFocusTarget | null;
  setFocusSuspendAutoRotate: (v: boolean) => void;
  exitCleanStage: () => void;
}

export const useGlobeStore = create<GlobeUiState>()(
  persist(
    (set, get) => ({
      selectedPlaylistIds: [],
      countryPanel: null,
      layers: defaultLayerState(),
      layersCollapsed: false,
      focusedPointId: null,
      pendingFocus: null,
      focusSuspendAutoRotate: false,

      selectPlaylist: (id) => {
        const cur = get().selectedPlaylistIds;
        if (cur.includes(id)) return;
        const next = [...cur, id];
        while (next.length > MAX_SELECTED_PLAYLISTS) next.shift();
        set({ selectedPlaylistIds: next });
      },

      deselectPlaylist: (id) => {
        set({
          selectedPlaylistIds: get().selectedPlaylistIds.filter((x) => x !== id),
          countryPanel:
            get().countryPanel?.playlistId === id
              ? { ...get().countryPanel!, playlistId: null, playlistName: null }
              : get().countryPanel,
        });
      },

      togglePlaylist: (id) => {
        if (get().selectedPlaylistIds.includes(id)) get().deselectPlaylist(id);
        else get().selectPlaylist(id);
      },

      setCountryPanel: (countryPanel) => set({ countryPanel }),
      clearCountryPanel: () => set({ countryPanel: null }),

      setLayer: (id, on) => {
        const layers = { ...get().layers, [id]: on };
        if (id === "cleanStage" && !on) {
          layers.focusedOnly = false;
        }
        set({ layers });
      },

      toggleLayer: (id) => {
        get().setLayer(id, !get().layers[id]);
      },

      setLayersCollapsed: (layersCollapsed) => set({ layersCollapsed }),
      setFocusedPointId: (focusedPointId) => set({ focusedPointId }),

      requestFocus: (pendingFocus) => set({ pendingFocus }),

      consumePendingFocus: () => {
        const t = get().pendingFocus;
        if (t) set({ pendingFocus: null });
        return t;
      },

      setFocusSuspendAutoRotate: (focusSuspendAutoRotate) =>
        set({ focusSuspendAutoRotate }),

      exitCleanStage: () => {
        const layers = { ...get().layers, cleanStage: false, focusedOnly: false };
        set({ layers });
      },
    }),
    {
      name: "coykoi-globe",
      partialize: (s) => ({
        layers: s.layers,
        layersCollapsed: s.layersCollapsed,
      }),
      merge: (persisted, current) => {
        const p = persisted as Partial<GlobeUiState> | undefined;
        return {
          ...current,
          ...p,
          layers: { ...defaultLayerState(), ...p?.layers },
          layersCollapsed: p?.layersCollapsed ?? current.layersCollapsed,
        };
      },
    },
  ),
);
